import React, { useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useNavigate } from "react-router-dom";
import analyticsService from "../services/analytics.service";

const STUDY_ORG_ID = process.env.REACT_APP_STUDY_ORG_ID;

function buildCrossSubdomainOrigin(fromPrefix, toPrefix) {
  const hostname = window.location.hostname;
  if (!hostname.startsWith(fromPrefix + '.')) return null;
  const rest = hostname.slice(fromPrefix.length + 1);
  const port = window.location.port ? ':' + window.location.port : '';
  return `${window.location.protocol}//${toPrefix}.${rest}${port}`;
}

function isOrgMembershipError(error) {
  const msg = error?.message || '';
  return msg.includes('is not part of the') && msg.includes('organization');
}

export const CallbackPage = () => {
  const { isLoading, error, isAuthenticated, user, logout } = useAuth0();
  const navigate = useNavigate();
  
  useEffect(() => {
    if (!isLoading && !error) {
      analyticsService.trackAuth('login_callback', 'auth0');
    }
  }, [isLoading, error]);

  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      // Customer user landed on app.* → action removed them → org error
      // is handled in the error effect below. This effect handles the
      // reverse: study-only user landed on platform.* and got assigned
      // the study org. Redirect them to app.* where they belong.
      if (window.location.hostname.startsWith('platform.')) {
        const orgId = user?.['cosigned/org_id'];
        if (!orgId || orgId === STUDY_ORG_ID) {
          const appOrigin = buildCrossSubdomainOrigin('platform', 'app');
          if (appOrigin) {
            window.location.href = appOrigin;
            return;
          }
        }
      }

      const returnTo = sessionStorage.getItem('auth_return_to') || '/dashboard';
      sessionStorage.removeItem('auth_return_to');
      navigate(returnTo, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, user]);

  useEffect(() => {
    if (error) {
      if (isOrgMembershipError(error)) {
        const platformOrigin = buildCrossSubdomainOrigin('app', 'platform');
        if (platformOrigin) {
          window.location.href = platformOrigin;
          return;
        }
      }
      console.error('Authentication error:', error.message);

      const params = new URLSearchParams(window.location.search);
      const description = params.get('error_description') || error.message;
      const returnUrl = `${window.location.origin}/?auth_error=${encodeURIComponent(description)}`;

      // Full Auth0 logout: clears the session cookie on the Auth0 domain
      // so the user can retry the invite with a different account.
      logout({ logoutParams: { returnTo: returnUrl } });
    }
  }, [error, navigate, logout]);

  return null;
};
